import { string } from "yup";

/**
 * -------------------------- String Validation Tests --------------------------
 *
 * This is where we declare predefined functions to be used in components and
 * forms that require name validation (see PasswordMeter.tsx).
 *
 * If a page requires a test that does not already exist, it can be added below.
 */

export enum _INTERNAL_TEST_ {
  NOT_BLANK,
  MIN_2_CHARS,
  MIN_8_CHARS,
  MAX_32_CHARS,
  MAX_63_CHARS,
  MAX_72_CHARS,
  MAX_253_CHARS,
  MAX_255_CHARS,
  NO_SPECIAL_START,
  NO_SPECIAL_OR_NUMERIC_START,
  NO_SPECIAL_END,
  NO_SPECIAL_OR_NUMERIC_END,
  HAS_ALPHABETIC_OR_UNDERSCORE_START,
  HAS_SIX_CHARS,
  HAS_ONE_NUMBER,
  HAS_ONE_UPPERCASE,
  HAS_6_UNIQUE_CHARS,
  HAS_ONLY_NUMBERS,
  IS_VALID_DOMAIN,
  IS_VALID_DOMAIN_OR_WILDCARD,
  ONLY_ALPHANUMERIC_AND_UNDERSCORES,
  ONLY_ALPHANUMERIC_AND_DASHES,
  ONLY_ALPHANUMERIC_AND_DASHES_AND_PERIODS,
  ONLY_LOWERCASE_ALPHANUMERIC_AND_DASHES,
  ONLY_LOWERCASE_ALPHANUMERIC_AND_DASHES_AND_PERIODS,
}

type Tests = Record<string, { text: string; evaluate(value: string): boolean }>;

export const TESTS: Tests = {
  [_INTERNAL_TEST_.NOT_BLANK]: {
    text: "Cannot be blank",
    evaluate: (value: string) => {
      return typeof value === "string" && value.length > 0;
    },
  },

  [_INTERNAL_TEST_.HAS_SIX_CHARS]: {
    text: "Must be 6 characters long",
    evaluate: (value: string) => {
      return typeof value === "string" && value.length === 6;
    },
  },

  [_INTERNAL_TEST_.HAS_ONLY_NUMBERS]: {
    text: "Can only contain numbers",
    evaluate: (value: string) => {
      return /^\d+$/.test(value);
    },
  },

  [_INTERNAL_TEST_.MIN_2_CHARS]: {
    text: "Must be at least 2 characters long",
    evaluate: (value: string) => {
      return typeof value === "string" && value.length >= 2;
    },
  },

  [_INTERNAL_TEST_.MIN_8_CHARS]: {
    text: "Must be at least 8 characters long",
    evaluate: (value: string) => {
      return typeof value === "string" && value.length >= 8;
    },
  },

  [_INTERNAL_TEST_.MAX_32_CHARS]: {
    text: "Cannot exceed 32 characters",
    evaluate: (value: string) => {
      return typeof value === "string" && value.length <= 32;
    },
  },

  [_INTERNAL_TEST_.MAX_63_CHARS]: {
    text: "Cannot exceed 63 characters",
    evaluate: (value: string) => {
      return typeof value === "string" && value.length <= 63;
    },
  },

  [_INTERNAL_TEST_.MAX_72_CHARS]: {
    text: "Cannot exceed 72 characters",
    evaluate: (value: string) => {
      return typeof value === "string" && value.length <= 72;
    },
  },

  [_INTERNAL_TEST_.MAX_253_CHARS]: {
    text: "Cannot exceed 253 characters",
    evaluate: (value: string) => {
      return typeof value === "string" && value.length <= 253;
    },
  },

  [_INTERNAL_TEST_.MAX_255_CHARS]: {
    text: "Cannot exceed 255 characters",
    evaluate: (value: string) => {
      return typeof value === "string" && value.length <= 255;
    },
  },

  [_INTERNAL_TEST_.NO_SPECIAL_START]: {
    text: "Cannot begin with a special character",
    evaluate: (value: string) => {
      return !/^[^A-Za-z0-9]/.test(value);
    },
  },

  [_INTERNAL_TEST_.NO_SPECIAL_OR_NUMERIC_START]: {
    text: "Cannot begin with a number or special character",
    evaluate: (value: string) => {
      return !/^[^A-Za-z]/.test(value);
    },
  },

  [_INTERNAL_TEST_.NO_SPECIAL_END]: {
    text: "Cannot end with a special character",
    evaluate: (value: string) => {
      return !/[^A-Za-z0-9]$/.test(value);
    },
  },

  [_INTERNAL_TEST_.NO_SPECIAL_OR_NUMERIC_END]: {
    text: "Cannot end with a number or special character",
    evaluate: (value: string) => {
      return !/[^A-Za-z]$/.test(value);
    },
  },

  [_INTERNAL_TEST_.HAS_ALPHABETIC_OR_UNDERSCORE_START]: {
    text: "Must start with an alphabetic character or underscore",
    evaluate: (value: string) => {
      return !/^[^A-Za-z_]/.test(value);
    },
  },

  [_INTERNAL_TEST_.HAS_ONE_NUMBER]: {
    text: "Must contain 1 number",
    evaluate: (value: string) => {
      return /[0-9]/.test(value);
    },
  },

  [_INTERNAL_TEST_.HAS_ONE_UPPERCASE]: {
    text: "Must contain 1 uppercase (first and last characters don’t count)",
    evaluate: (value: string) => {
      return /(?!^)[A-Z](?!$)/.test(value);
    },
  },

  [_INTERNAL_TEST_.HAS_6_UNIQUE_CHARS]: {
    text: "Must contain at least 6 unique characters",
    evaluate: (value: string) => {
      return new Set(value).size >= 6;
    },
  },

  [_INTERNAL_TEST_.IS_VALID_DOMAIN]: {
    text: "Must be a valid domain or subdomain",
    // Taken from:
    // https://github.internal.digitalocean.com/digitalocean/cthulhu/pull/71693
    evaluate: (value: string) => {
      return /^([a-zA-Z0-9]+(-+[a-zA-Z0-9]+)*\.)+(xn--)?[a-zA-Z0-9]{2,}\.?$/.test(value);
    },
  },

  [_INTERNAL_TEST_.IS_VALID_DOMAIN_OR_WILDCARD]: {
    // Adapted from:
    // https://github.internal.digitalocean.com/digitalocean/cthulhu/pull/71693
    text: "Must be a valid domain or subdomain",
    evaluate: (value: string) => {
      return /^((\*\.)|([a-zA-Z0-9]+(-+[a-zA-Z0-9]+)*\.))+(xn--)?[a-zA-Z0-9]{2,}\.?$/.test(value);
    },
  },

  [_INTERNAL_TEST_.ONLY_ALPHANUMERIC_AND_UNDERSCORES]: {
    text: "Can only contain alphanumeric characters and underscores",
    evaluate: (value: string) => {
      return !/[^A-Za-z0-9_]/.test(value);
    },
  },

  [_INTERNAL_TEST_.ONLY_ALPHANUMERIC_AND_DASHES]: {
    text: "Can only contain alphanumeric characters and dashes",
    evaluate: (value: string) => {
      return !/[^A-Za-z0-9-]/.test(value);
    },
  },

  [_INTERNAL_TEST_.ONLY_ALPHANUMERIC_AND_DASHES_AND_PERIODS]: {
    text: "Can only contain alphanumeric characters, dashes, and periods",
    evaluate: (value: string) => {
      return !/[^A-Za-z0-9\-.]/.test(value);
    },
  },

  [_INTERNAL_TEST_.ONLY_LOWERCASE_ALPHANUMERIC_AND_DASHES]: {
    text: "Can only contain lowercase alphanumeric characters and dashes",
    evaluate: (value: string) => {
      return !/[^a-z0-9-]/.test(value);
    },
  },

  [_INTERNAL_TEST_.ONLY_LOWERCASE_ALPHANUMERIC_AND_DASHES_AND_PERIODS]: {
    text: "Can only contain lowercase alphanumeric characters, dashes, and periods",
    evaluate: (value: string) => {
      return !/[^a-z0-9\-.]/.test(value);
    },
  },
};

/**
 * ------------------------ String Validation Schemas -------------------------
 *
 * This is where we declare our predefined schemas. If a page requires a schema
 * that does not already exist, it can be added below.
 */

export enum SCHEMA {
  PASSWORD_DEFAULT,
  PASSWORD_ACCOUNT,
  PASSWORD_DROPLET,
  APPS_PLATFORM_APP_NAME,
  APPS_PLATFORM_DB_NAME,
  APPS_PLATFORM_DOMAIN_NAME,
  APPS_PLATFORM_ENV_KEY,
  VPC_NAME,
  SIX_DIGIT_CODE,
}

export const SCHEMAS = {
  [SCHEMA.PASSWORD_DEFAULT]: [_INTERNAL_TEST_.MIN_8_CHARS, _INTERNAL_TEST_.HAS_6_UNIQUE_CHARS],

  [SCHEMA.PASSWORD_ACCOUNT]: [_INTERNAL_TEST_.MIN_8_CHARS, _INTERNAL_TEST_.HAS_6_UNIQUE_CHARS, _INTERNAL_TEST_.MAX_72_CHARS],

  [SCHEMA.SIX_DIGIT_CODE]: [_INTERNAL_TEST_.HAS_SIX_CHARS, _INTERNAL_TEST_.HAS_ONLY_NUMBERS],

  [SCHEMA.PASSWORD_DROPLET]: [
    _INTERNAL_TEST_.MIN_8_CHARS,
    _INTERNAL_TEST_.HAS_ONE_UPPERCASE,
    _INTERNAL_TEST_.HAS_ONE_NUMBER,
    _INTERNAL_TEST_.NO_SPECIAL_OR_NUMERIC_END,
  ],

  [SCHEMA.APPS_PLATFORM_APP_NAME]: [
    _INTERNAL_TEST_.NOT_BLANK,
    _INTERNAL_TEST_.NO_SPECIAL_OR_NUMERIC_START,
    _INTERNAL_TEST_.ONLY_LOWERCASE_ALPHANUMERIC_AND_DASHES,
    _INTERNAL_TEST_.MIN_2_CHARS,
    _INTERNAL_TEST_.MAX_32_CHARS,
    _INTERNAL_TEST_.NO_SPECIAL_END,
  ],

  [SCHEMA.APPS_PLATFORM_DB_NAME]: [
    _INTERNAL_TEST_.NOT_BLANK,
    _INTERNAL_TEST_.NO_SPECIAL_OR_NUMERIC_START,
    _INTERNAL_TEST_.ONLY_LOWERCASE_ALPHANUMERIC_AND_DASHES,
    _INTERNAL_TEST_.MIN_2_CHARS,
    _INTERNAL_TEST_.MAX_63_CHARS,
    _INTERNAL_TEST_.NO_SPECIAL_END,
  ],

  [SCHEMA.APPS_PLATFORM_DOMAIN_NAME]: [
    _INTERNAL_TEST_.NOT_BLANK,
    _INTERNAL_TEST_.MAX_253_CHARS,
    _INTERNAL_TEST_.IS_VALID_DOMAIN_OR_WILDCARD,
  ],

  [SCHEMA.APPS_PLATFORM_ENV_KEY]: [_INTERNAL_TEST_.HAS_ALPHABETIC_OR_UNDERSCORE_START, _INTERNAL_TEST_.ONLY_ALPHANUMERIC_AND_UNDERSCORES],

  [SCHEMA.VPC_NAME]: [
    _INTERNAL_TEST_.NOT_BLANK,
    _INTERNAL_TEST_.NO_SPECIAL_START,
    _INTERNAL_TEST_.ONLY_ALPHANUMERIC_AND_DASHES_AND_PERIODS,
    _INTERNAL_TEST_.MIN_2_CHARS,
    _INTERNAL_TEST_.MAX_255_CHARS,
    _INTERNAL_TEST_.NO_SPECIAL_END,
  ],
};

/**
 * Accepts, modifies, and returns a Yup String object that includes
 * a test for each function in a predefined schema. This function
 * should be used whenever a schema is implemented, as this will keep
 * our error messaging consistent.
 *
 * If a Yup String is not passed in, this function will generate a new
 * Yup String by default.
 */
export const stringValidation = (schema: SCHEMA, validator = string()) => {
  SCHEMAS[schema].forEach((test) => {
    validator = validator.test("valid", TESTS[test].text, TESTS[test].evaluate);
  });
  return validator;
};
